import {v4 as uuidv4} from 'uuid';
import {deleteSessionFile, storeSessionFile} from 'src/offline/files';
import {getActiveSession} from 'src/offline/offlineSessionManager';
import {
  collectPendingRefs,
  enqueuePendingUploads,
  hasPendingUploads,
} from 'src/offline/pendingUploads';
import type {PendingUploadRef} from 'src/offline/pendingUploads';
import {ensureCapacity} from 'src/offline/quota';
import {FORMS_CONTEXT_KEY} from './snapshotKeys';

export const FORM_OUTBOX_KINDS = {
  create: 'form.create',
} as const;

/** The pending upload refs a form create payload still carries. */
export const formUploadRefs = (payload: unknown): PendingUploadRef[] =>
  collectPendingRefs(
    ((payload as {targetFieldValues?: {value?: string}[]}).targetFieldValues ?? []).map(
      fieldValue => fieldValue.value
    )
  );

export const hasPendingFormUploads = (
  fieldValues: {value?: string}[]
): boolean => hasPendingUploads(fieldValues.map(fieldValue => fieldValue.value));

/** Queues one upload item per pending capture in the form's field values. */
export const enqueuePendingFormUploads = (
  sessionId: string,
  fieldValues: {value?: string}[]
): Promise<void> =>
  enqueuePendingUploads(
    sessionId,
    fieldValues.map(fieldValue => fieldValue.value)
  );

// Persists a captured blob under the forms session and returns its refKey; the
// placeholder entry in the field value carries the same key back to sync.
export const storeCapturedFormFile = async (
  blob: Blob,
  name: string,
  type: string
): Promise<string | undefined> => {
  const session = await getActiveSession(FORMS_CONTEXT_KEY);
  if (!session?.ready) return undefined;
  await ensureCapacity(blob.size);
  const refKey = uuidv4();
  await storeSessionFile({
    sessionId: session.id,
    kind: 'captured',
    refKey,
    name,
    contentType: type,
    blob,
  });
  return refKey;
};

/** Removes captured blobs when pending attachments are deleted before sync. */
export const deleteCapturedFormFiles = async (
  refKeys: Iterable<string>
): Promise<void> => {
  const unique = [...new Set(refKeys)];
  if (unique.length === 0) return;
  const session = await getActiveSession(FORMS_CONTEXT_KEY);
  if (!session) return;
  await Promise.all(
    unique.map(refKey => deleteSessionFile(session.id, refKey))
  );
};

// Frees every captured blob still referenced by a set of field values — used
// when an offline create is abandoned before its uploads were queued.
export const deleteUnsavedFormUploads = (
  values: Record<string, unknown>
): Promise<void> =>
  deleteCapturedFormFiles(
    collectPendingRefs(Object.values(values)).map(ref => ref.refKey)
  );
